import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';

const ACTOR_TYPES = ['caller_id', 'email', 'ip', 'extension'];

export default function Blocklist() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ actor_type: 'caller_id' });

  const { data: list = [] } = useQuery<any[]>({
    queryKey: ['blocklist'],
    queryFn: async () =>
      fetch('/api/blocklist', { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => r.json()),
  });
  const create = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/blocklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['blocklist'] }); setOpen(false); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => fetch(`/api/blocklist/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocklist'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Blocklist</h1>
        <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Block</button>
      </div>
      <p className="text-sm text-textSecondary">Block specific callers, emails, IPs, or extensions from initiating contact. Different from CID Blacklist (always-block-this-caller-id) and IP Blacklist (network-level).</p>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Type</th><th>Value</th><th>Reason</th><th>By</th><th>Blocked at</th><th>Expires</th><th></th></tr></thead>
          <tbody>
            {list.map((b: any) => (
              <tr key={b.block_uuid} className="hover:bg-elevated">
                <td>{b.actor_type}</td>
                <td className="font-mono">{b.actor_value}</td>
                <td>{b.reason || '—'}</td>
                <td>{b.blocked_by || '—'}</td>
                <td className="text-xs">{new Date(b.blocked_at).toLocaleString()}</td>
                <td className="text-xs">{b.expires_at ? new Date(b.expires_at).toLocaleString() : 'never'}</td>
                <td><button onClick={() => remove.mutate(b.block_uuid)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onClose={() => setOpen(false)} title="Block actor" size="md"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Block</button></>}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Type</label>
            <select className="input" value={form.actor_type} onChange={(e) => setForm({ ...form, actor_type: e.target.value })}>
              {ACTOR_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Value *</label>
            <input className="input font-mono" value={form.actor_value || ''} onChange={(e) => setForm({ ...form, actor_value: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Reason (optional)</label>
            <input className="input" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Expires (optional)</label>
            <input className="input" type="datetime-local" value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
